import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validationApi } from '../services/validationApi';
import { MISSING_LABELS } from '../utils/documentationGaps';
import { isEndState } from '../utils/yesNo';
import { JumpOrigin } from '../utils/fieldAnchors';
import type { ValidationStatusResponse, ValidationDecision, Finding, Fix } from '../types/validation';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { formatIsoDate } from '../utils/formatDateValue';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  InformationCircleIcon,
  SparklesIcon,
  PencilSquareIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { enumLabel } from '../utils/enumLabel';

export type DrawerTab = 'all' | 'blockers' | 'resolution' | 'autofilled' | 'notes' | 'coverage' | 'history';

// Imperative API for the page: open the details drawer on a tab, or the completeness slide-over.
export interface AiValidationPanelHandle {
  openDrawer: (tab?: DrawerTab) => void;
  openCompleteness: () => void;
}

export const decisionColors: Record<ValidationDecision, string> = {
  VALIDATED: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  NEEDS_REVIEW: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

const decisionIcons: Record<ValidationDecision, React.ReactNode> = {
  VALIDATED: <CheckCircleIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />,
  REJECTED: <XCircleIcon className="h-4 w-4 text-red-500" />,
  NEEDS_REVIEW: <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />,
};

export function decisionLabel(decision: ValidationDecision) {
  if (decision === 'NEEDS_REVIEW') return 'Needs Review';
  return decision.charAt(0) + decision.slice(1).toLowerCase();
}

// Bar sizes: 'sm' fills its column (sidebar card), 'lg' is the drawer header's, 'md' the page's
// AI card, where the two bars share a row with the card's heading.
type BarSize = 'sm' | 'md' | 'lg';
const BAR_TRACK: Record<BarSize, string> = { sm: 'w-full h-2', md: 'w-24 h-2.5', lg: 'w-32 h-2.5' };
const barValueClass = (size: BarSize) => size === 'sm' ? 'text-xs text-gray-600 dark:text-gray-400' : 'text-sm text-gray-700 dark:text-gray-300';

function confidenceBar(confidence: number, size: BarSize = 'sm') {
  const pct = Math.round(confidence * 100);
  // Neutral by default; red only when confidence is low enough to be a warning in itself.
  const color = pct < 50 ? 'bg-red-500' : 'bg-gray-500 dark:bg-gray-400';
  return (
    <div className="flex items-center gap-2">
      <div className={`bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden ${BAR_TRACK[size]}`}>
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`font-medium whitespace-nowrap ${barValueClass(size)}`}>{pct}%</span>
    </div>
  );
}

// A qualitative word for how much of the case is documented — informational, so it doesn't read as
// a "fill the remaining fields" task the way a raw fraction does.
function coverageBand(pct: number): string {
  if (pct >= 90) return 'Extensive';
  if (pct >= 70) return 'Good';
  if (pct >= 40) return 'Partial';
  return 'Limited';
}

// Structural documentation-coverage bar (a separate axis from confidence — how fully documented, not
// how trustworthy). Shows the qualitative band rather than a fraction to avoid implying a to-do.
function completenessBar(c: { completeness: number; filled: number; total: number } | null | undefined, size: BarSize = 'sm') {
  if (!c) return null;
  const pct = Math.round(c.completeness * 100);
  return (
    <div className="flex items-center gap-2">
      <div className={`bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden ${BAR_TRACK[size]}`}>
        <div className="h-full bg-gray-500 dark:bg-gray-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className={`font-medium whitespace-nowrap ${barValueClass(size)}`}>{coverageBand(pct)}</span>
    </div>
  );
}

// Friendly labels for the completeness "not documented" field paths, grouped by section so the
// raw dotted keys (and the per-violation repeats) read as plain language.
// MISSING_LABELS lives in utils/documentationGaps so the page's placeholders read the same.
const missingSection = (p: string): string =>
  p.startsWith('victim.violation.') ? 'Per-violation demographics'
    : p.startsWith('victim.') ? 'Victim'
    : p.startsWith('violation.') ? 'Violation'
    : 'Case';
const missingLabel = (p: string): string => {
  const leaf = p.split('.').slice(-1)[0];
  return MISSING_LABELS[leaf] || leaf.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
};
// Group missing fields by section and collapse identical labels into a count (× N).
function groupMissing(paths: string[]): { section: string; items: { label: string; count: number }[] }[] {
  const order = ['Case', 'Victim', 'Violation', 'Per-violation demographics'];
  const bySection = new Map<string, Map<string, number>>();
  for (const p of paths) {
    const sec = missingSection(p);
    const lab = missingLabel(p);
    if (!bySection.has(sec)) bySection.set(sec, new Map());
    const m = bySection.get(sec)!;
    m.set(lab, (m.get(lab) || 0) + 1);
  }
  return order.filter((s) => bySection.has(s)).map((s) => ({
    section: s,
    items: Array.from(bySection.get(s)!.entries()).map(([label, count]) => ({ label, count })),
  }));
}

// The four hard extraction anchors — pass/fail, separate from coverage %.
export const ANCHOR_LABELS: Record<string, string> = {
  incidentStartDate: 'Start date',
  namedIndigenousGroup: 'Indigenous group',
  location: 'Location',
  identifiableVictim: 'Identifiable victim',
};
function AnchorsBadge({ comp }: { comp?: { anchorsPass?: boolean; missingAnchors?: string[] } | null }) {
  if (!comp || comp.anchorsPass === undefined) return null;
  if (comp.anchorsPass) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200" title="All four required extraction anchors are present">
        ✓ Anchors complete
      </span>
    );
  }
  const missing = (comp.missingAnchors || []).map((a) => ANCHOR_LABELS[a] || a).join(', ');
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Required extraction anchors (Case Baseline / Data Dictionary) are missing">
      Missing anchor{(comp.missingAnchors || []).length > 1 ? 's' : ''}: {missing}
    </span>
  );
}

// A labeled row: "<label>" left, bar right.
function labeledBar(label: string, bar: React.ReactNode) {
  if (!bar) return null;
  return (
    <div>
      <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      {bar}
    </div>
  );
}

// "Why this score" — the deterministic deductions that produced the confidence number, as a
// one-line summary that expands to the list. Collapsed by default so the score block stays short.
function ScoreBreakdown({ breakdown }: { breakdown?: { method: string; aiSelfEstimate?: number; deductions?: Array<{ category: string; weight: number; message?: string | null }> } | null }) {
  const [open, setOpen] = useState(false);
  if (!breakdown || breakdown.method !== 'deterministic') return null;
  const ded = breakdown.deductions || [];
  const label = (c: string) => c.replace(/_/g, ' ').toLowerCase().replace(/^./, (x) => x.toUpperCase());
  const total = Math.round(ded.reduce((sum, d) => sum + d.weight, 0) * 100);
  if (ded.length === 0) {
    return <p className="text-xs text-gray-500 dark:text-gray-400">No deductions: clean, corroborated case.</p>;
  }
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
      >
        <span className="text-red-600 dark:text-red-400 font-medium">−{total}%</span>
        <span>from {ded.length} deduction{ded.length === 1 ? '' : 's'}</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul className="mt-1.5 space-y-0.5 pl-1">
          {ded.map((d, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className="text-red-600 dark:text-red-400 font-medium whitespace-nowrap">−{Math.round(d.weight * 100)}%</span>
              <span className="text-gray-600 dark:text-gray-300">{label(d.category)}{d.message ? ` — ${d.message}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function getValueByPath(obj: any, path: string): unknown {
  // Split on dots and brackets: "victims[uuid].field" → ["victims", "uuid", "field"]
  const keys = path.replace(/\[([^\]]+)\]/g, '.$1').split('.');
  return keys.reduce((curr, key) => {
    if (curr === null || curr === undefined) return undefined;
    // If current value is an array and key is not a numeric index, look up by id
    if (Array.isArray(curr) && !/^\d+$/.test(key)) {
      return curr.find((item: any) => item?.id === key);
    }
    return curr[key];
  }, obj);
}

const fieldLabels: Record<string, string> = {
  country: 'Country', region: 'Region', city: 'City', specificLocation: 'Specific Location',
  description: 'Description', familyImpact: 'Family Impact', communityImpact: 'Community Impact',
  incidentTime: 'Incident Time', caseStartDate: 'Start Date', caseEndDate: 'End Date',
  reportedDate: 'Reported Date', violationTypes: 'Violation Types', otherViolationTypes: 'Other Violation Types',
  perpetratorKnown: 'Perpetrator Known', perpetratorTypes: 'Perpetrator Types',
  perpetratorDetails: 'Perpetrator Details', legalCaseFiled: 'Legal Case Filed',
  legalCaseDetails: 'Legal Case Details', legalCaseWhyNot: 'Legal Case (Why Not)',
  stateActionUndertaken: 'State Action Undertaken', stateActionDetails: 'State Action Details',
  stateActionWhyNot: 'State Action (Why Not)', whyVictimsCriminalized: 'Why Victims Criminalized',
  accusations: 'Accusations', policiesOrLawsUsed: 'Policies/Laws Used',
  whoFiledAccusations: 'Who Filed Accusations', accusationsLegallyFiled: 'Accusations Legally Filed',
  courtFilingLocation: 'Court Filing Location', courtFilingDetails: 'Court Filing Details',
  decisionOnAccusations: 'Decision on Accusations', decisionDetails: 'Decision Details',
  victimsInPrison: 'Victims in Prison', prisonConditions: 'Prison Conditions',
  victimsSituationIfNotInPrison: 'Victims Situation', whyVictimsKilled: 'Why Victims Killed',
  howKillingCarriedOut: 'How Killing Carried Out', cause: 'Cause',
  harassmentPrior: 'Prior Harassment', harassmentPriorDetails: 'Prior Harassment Details',
  investigationConducted: 'Investigation Conducted', investigationDetails: 'Investigation Details',
  investigationWhyNot: 'Investigation (Why Not)', advocacyUndertaken: 'Advocacy Undertaken',
  advocacyDetails: 'Advocacy Details', advocacyWhyNot: 'Advocacy (Why Not)',
  indigenousGroup: 'Indigenous Group', indigenousTerritory: 'Indigenous Territory',
  firstName: 'First Name', lastName: 'Last Name', gender: 'Gender',
  communityRole: 'Community Role', additionalInfo: 'Additional Info',
  indigenousGroupOfVictim: 'Indigenous Group', isIndigenous: 'Indigenous', name: 'Name', type: 'Type',
  affiliation: 'Affiliation', notes: 'Notes',
  developmentProjects: 'Development Project', subtypes: 'Subtypes', startYear: 'Start Year', websiteLink: 'Website', source: 'Source',
  criminalizationTypes: 'Form of Criminalization', criminalizationTriggers: 'Criminalized Activities',
  legalInstrumentTypes: 'Legal Instrument Categories', proceedingType: 'Type of Proceeding',
  isRetaliatoryProceeding: 'Retaliatory Proceeding', perpetratorAccountabilityStatus: 'Perpetrator Accountability',
  accusationOutcome: 'Outcome of Accusations', detentionType: 'Detention', detentionDays: 'Days in Detention',
  dueProcessViolations: 'Due-Process Violations', groupType: 'Type of Group',
  communityRoleType: 'Community Role Category', impactTypes: 'Types of Impact',
};

const violationTypeLabels: Record<string, string> = {
  KILLING: 'Killing', CRIMINALIZATION: 'Criminalization', OTHER: 'Other',
};

/** Turn "violationDetails[uuid].accusations" into "Criminalization > Accusations" */
export function formatFieldPath(path: string, caseData?: Record<string, any>): string {
  const segments = path.replace(/\[([^\]]+)\]/g, '.$1').split('.');
  const parts: string[] = [];

  let i = 0;
  while (i < segments.length) {
    const seg = segments[i];

    if (seg === 'violationDetails') {
      // Next segment is the ID, look up the violation type
      const id = segments[i + 1];
      let label = 'Violation Details';
      if (id && caseData?.violationDetails) {
        const detail = (caseData.violationDetails as any[]).find((d: any) => d.id === id);
        if (detail?.violationType) {
          label = violationTypeLabels[detail.violationType] || detail.violationType;
        }
      }
      parts.push(label);
      i += 2; // skip collection + id
    } else if (seg === 'victims') {
      const id = segments[i + 1];
      let label = 'Victims';
      if (id && caseData?.victims) {
        const victim = (caseData.victims as any[]).find((v: any) => v.id === id);
        if (victim) {
          const name = [victim.firstName, victim.lastName].filter(Boolean).join(' ');
          if (name) label = name;
        }
      }
      parts.push(label);
      i += 2;
    } else if (seg === 'perpetrators') {
      const id = segments[i + 1];
      let label = 'Perpetrators';
      if (id && caseData?.perpetrators) {
        const perp = (caseData.perpetrators as any[]).find((p: any) => p.id === id);
        if (perp?.name) label = perp.name;
      }
      parts.push(label);
      i += 2;
    } else if (seg === 'violation' && segments[i + 1] && /^[A-Z][A-Z0-9_]*$/.test(segments[i + 1])) {
      // Per-violation row on a victim: "violation[PHYSICAL_INJURY]" → "Violation: Physical Injury"
      parts.push(`Violation: ${formatEnumValue(segments[i + 1])}`);
      i += 2;
    } else if (/^[A-Z][A-Z0-9_]*$/.test(seg)) {
      // A bare enum key (e.g. "KILLING") — title-case it, don't space out the capitals.
      parts.push(formatEnumValue(seg));
      i++;
    } else {
      // Regular camelCase field name
      parts.push(fieldLabels[seg] || seg.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim());
      i++;
    }
  }

  return parts.join(' › ');
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'none';
  if (Array.isArray(value) && value.length === 0) return 'none';
  const asDate = formatIsoDate(value);
  if (asDate) return asDate;
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

/** Format an UPPER_SNAKE_CASE enum value to Title Case. */
function formatEnumValue(val: string): string {
  return enumLabel(val);
}

/** Check if a string looks like an UPPER_SNAKE_CASE enum value. */
function isEnumValue(val: string): boolean {
  return /^[A-Z][A-Z0-9_]+$/.test(val);
}

/** Summarise a single record object into key–value pairs, dropping nulls and internal fields. */
function summarizeRecord(obj: Record<string, unknown>): Array<{ label: string; raw: unknown }> {
  const skip = new Set(['id', 'caseId', 'createdAt', 'updatedAt', 'isGroup', 'groupName', 'groupSize',
    'affectedIndividualsCount', 'affectedFamiliesCount', 'affectedHomesCount', 'affectedGroupsCount',
    'affectedCommunitiesCount', 'affectedVillagesCount', 'affectedMaleCount', 'affectedFemaleCount',
    'affected0to12Count', 'affected13to25Count', 'affected26to35Count', 'affected36to59Count', 'affected60PlusCount',
    'violationType']);
  const labelMap: Record<string, string> = {
    firstName: 'First Name', lastName: 'Last Name', gender: 'Gender',
    dateOfBirth: 'Date of Birth', ageBracket: 'Age Bracket',
    dependentType: 'Dependent Type', dependentDetails: 'Dependent Details',
    communityRole: 'Community Role', indigenousGroupOfVictim: 'Indigenous Group', isIndigenous: 'Indigenous',
    additionalInfo: 'Additional Info', name: 'Name', type: 'Type',
    affiliation: 'Affiliation', position: 'Position', notes: 'Notes',
    isOrganization: 'Organization', organizationHead: 'Organization Head',
    perpetratorKnown: 'Perpetrator Known', perpetratorTypes: 'Perpetrator Types',
    perpetratorDetails: 'Perpetrator Details', legalCaseFiled: 'Legal Case Filed',
    legalCaseDetails: 'Legal Case Details', legalCaseWhyNot: 'Legal Case (Why Not)',
    stateActionUndertaken: 'State Action Undertaken', stateActionDetails: 'State Action Details',
    stateActionWhyNot: 'State Action (Why Not)', whyVictimsCriminalized: 'Why Victims Criminalized',
    accusations: 'Accusations', policiesOrLawsUsed: 'Policies/Laws Used',
    whoFiledAccusations: 'Who Filed Accusations', whoFiledAccusationsDetails: 'Who Filed Accusations Details',
    accusationsLegallyFiled: 'Accusations Legally Filed', courtFilingLocation: 'Court Filing Location',
    courtFilingDetails: 'Court Filing Details', decisionOnAccusations: 'Decision on Accusations',
    decisionDetails: 'Decision Details', victimsInPrison: 'Victims in Prison',
    prisonConditions: 'Prison Conditions', victimsSituationIfNotInPrison: 'Victims Situation',
    whyVictimsKilled: 'Why Victims Killed', howKillingCarriedOut: 'How Killing Carried Out',
    harassmentPrior: 'Prior Harassment', harassmentPriorDetails: 'Prior Harassment Details',
    cause: 'Cause',
    criminalizationTypes: 'Form of Criminalization', criminalizationTriggers: 'Criminalized Activities',
    legalInstrumentTypes: 'Legal Instrument Categories', proceedingType: 'Type of Proceeding',
    isRetaliatoryProceeding: 'Retaliatory Proceeding', perpetratorAccountabilityStatus: 'Perpetrator Accountability',
    groupType: 'Type of Group', communityRoleType: 'Community Role Category',
  };
  const entries: Array<{ label: string; raw: unknown }> = [];
  for (const [k, v] of Object.entries(obj)) {
    if (skip.has(k) || v === null || v === undefined || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    const label = labelMap[k] || k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
    entries.push({ label, raw: v });
  }
  return entries;
}

/** Render a field value with appropriate formatting (badges for enums/arrays, text for strings). */
function FieldValue({ value }: { value: unknown }) {
  if (typeof value === 'boolean') {
    return value
      ? <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">Yes</span>
      : <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">No</span>;
  }
  if (Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string')) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <span key={i} className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            {isEnumValue(v) ? formatEnumValue(v) : v}
          </span>
        ))}
      </div>
    );
  }
  if (typeof value === 'string' && isEnumValue(value)) {
    return <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{formatEnumValue(value)}</span>;
  }
  if (typeof value === 'string') {
    return <span className="text-xs text-gray-800 dark:text-gray-100 break-words">{value}</span>;
  }
  return <span className="text-xs text-gray-800 dark:text-gray-100 break-words">{JSON.stringify(value)}</span>;
}

/** Detect if value is a structured object (not a plain string/number). */
function isStructuredObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) &&
    Object.keys(value).length > 2;
}

/** Detect if value looks like an array of structured records or a single structured object. */
function isRecordArray(value: unknown): value is Array<Record<string, unknown>> {
  if (isStructuredObject(value)) return false; // single object handled separately
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  return typeof first === 'object' && first !== null &&
    ('firstName' in first || 'name' in first || 'isGroup' in first || 'isOrganization' in first || 'violationType' in first);
}

const violationTypeTitles: Record<string, string> = {
  KILLING: 'Killing', CRIMINALIZATION: 'Criminalization', OTHER: 'Other',
};

function getRecordTitle(obj: Record<string, unknown>): string {
  if (obj.violationType) return violationTypeTitles[String(obj.violationType)] || String(obj.violationType);
  if (obj.firstName || obj.lastName) {
    return [obj.firstName, obj.lastName].filter(Boolean).join(' ');
  }
  if (obj.name) return String(obj.name);
  if (obj.groupName && Array.isArray(obj.groupName) && obj.groupName.length > 0) {
    return (obj.groupName as string[]).join(', ');
  }
  return 'Record';
}

function RecordCard({ record, className }: { record: Record<string, unknown>; className?: string }) {
  const title = getRecordTitle(record);
  const titleKeys = new Set(['First Name', 'Last Name', 'Name', 'Violation Type']);
  const fields = summarizeRecord(record);
  return (
    <div className={`bg-white dark:bg-gray-800/60 rounded-md p-2 border border-gray-200 dark:border-gray-700/50 ${className || ''}`}>
      <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">{title}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {fields.filter(f => !titleKeys.has(f.label)).map(f => (
          <React.Fragment key={f.label}>
            <dt className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{f.label}</dt>
            <dd><FieldValue value={f.raw} /></dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

function RichValue({ value, className }: { value: unknown; className?: string }) {
  if (isStructuredObject(value)) {
    return <RecordCard record={value as Record<string, unknown>} className={className} />;
  }

  if (isRecordArray(value)) {
    return (
      <div className={`space-y-2 ${className || ''}`}>
        {value.map((record, i) => (
          <RecordCard key={i} record={record} className={className} />
        ))}
      </div>
    );
  }

  // Simple string array → badges
  if (Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string')) {
    return (
      <div className={`flex flex-wrap gap-1 ${className || ''}`}>
        {value.map((v, i) => (
          <span key={i} className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            {isEnumValue(v) ? formatEnumValue(v) : v}
          </span>
        ))}
      </div>
    );
  }

  // Single enum string → badge
  if (typeof value === 'string' && isEnumValue(value)) {
    return <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 ${className || ''}`}>{formatEnumValue(value)}</span>;
  }

  // Boolean
  if (typeof value === 'boolean') {
    return value
      ? <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 ${className || ''}`}>Yes</span>
      : <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 ${className || ''}`}>No</span>;
  }

  const formatted = formatValue(value);
  if (formatted === 'none') {
    return <span className={`italic text-gray-400 dark:text-gray-500 text-xs ${className || ''}`}>none</span>;
  }
  return <span className={`text-xs text-gray-800 dark:text-gray-100 ${className || ''}`}>{formatted}</span>;
}

// Derive the suggestion type for a fix (mirrors the merge-suggestion taxonomy):
//   Add     — a new child record (victim/perpetrator/violation detail)
//   Fill    — the field is currently empty
//   Conflict — the field already has a different value
// A field label that, when the page can jump to fields, becomes a "show me" link.
function FieldLink({ path, label, onJump, className }: { path: string; label: string; onJump?: (path: string) => void; className?: string }) {
  if (!onJump) return <>{label}</>;
  return (
    <button
      type="button"
      onClick={() => onJump(path)}
      title="Show this field on the page"
      className={`inline-flex items-center gap-0.5 text-left hover:underline ${className ?? ''}`}
    >
      {label}
      <ArrowRightIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
    </button>
  );
}

function deriveFixType(field: string, currentValue: unknown): { label: string; color: string } {
  if (['victims', 'perpetrators', 'violationDetails'].includes(field)) return { label: 'Add', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' };
  const empty = currentValue === null || currentValue === undefined || currentValue === '' || (Array.isArray(currentValue) && currentValue.length === 0);
  return empty ? { label: 'Fill', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' } : { label: 'Conflict', color: 'bg-red-100 text-red-700' };
}

// One layout for every drawer entry (blocker, conflict, auto-fill, undone, note, finding):
// pill + field on the first line, the message, a type-specific value block, an inline
// "Evidence" disclosure (snippet, source, former value) and an actions row. Tone drives the
// border and pill colour: red for blockers, amber for conflicts, neutral otherwise.
type EntryTone = 'danger' | 'warning' | 'neutral';
interface EntryEvidence { snippet?: string | null; source?: string | null; prior?: React.ReactNode; priorLabel?: string }
const TONE_PILL: Record<EntryTone, string> = {
  danger: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  neutral: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};
const TONE_BORDER: Record<EntryTone, string> = {
  danger: 'border-red-200 dark:border-red-800',
  warning: 'border-amber-200 dark:border-amber-800',
  neutral: 'border-gray-200 dark:border-gray-700',
};
function ReviewEntry({ tone, pill, category, field, caseData, onJump, message, messageHref, messageAsNote, meta, value, evidence, actions }: {
  tone: EntryTone;
  pill: string;
  category?: string | null;
  field?: string | null;
  caseData?: Record<string, any>;
  onJump?: (path: string) => void;
  message?: string | null;
  messageHref?: string | null;
  // The message is the AI's reasoning, not the finding: shown small and labelled so it reads
  // apart from the value underneath.
  messageAsNote?: boolean;
  meta?: React.ReactNode;
  value?: React.ReactNode;
  evidence?: EntryEvidence;
  actions?: React.ReactNode;
}) {
  const [showEvidence, setShowEvidence] = useState(false);
  const hasEvidence = !!(evidence && (evidence.snippet || evidence.source || evidence.prior !== undefined));
  const evidenceCount = evidence ? [evidence.snippet, evidence.source, evidence.prior !== undefined ? 1 : null].filter(Boolean).length : 0;
  return (
    <li className={`rounded-md border ${TONE_BORDER[tone]} bg-white dark:bg-gray-800 p-3 text-xs list-none`}>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded ${TONE_PILL[tone]}`}>{pill}</span>
          {category && category !== 'OTHER' && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {String(category).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())}
            </span>
          )}
        </div>
        {field && (
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <FieldLink path={field} label={formatFieldPath(field, caseData)} onJump={onJump} />
          </div>
        )}
      </div>
      {message && messageAsNote && (
        <div className="mt-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Why the AI filled it</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">{message}</div>
        </div>
      )}
      {message && !messageAsNote && (
        <div className={`mt-1.5 text-sm ${tone === 'danger' ? 'text-red-800 dark:text-red-200' : 'text-gray-800 dark:text-gray-200'}`}>
          {messageHref ? <a href={messageHref} target="_blank" rel="noreferrer" className="underline hover:no-underline">{message}</a> : message}
        </div>
      )}
      {value !== undefined && value !== null && <div className="mt-2">{value}</div>}
      {meta && <div className="mt-1.5">{meta}</div>}
      {hasEvidence && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowEvidence((v) => !v)}
            aria-expanded={showEvidence}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <InformationCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Evidence ({evidenceCount})
            <ChevronDownIcon className={`h-3 w-3 transition-transform ${showEvidence ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {showEvidence && (
            <div className="mt-1.5 rounded-md bg-gray-50 dark:bg-gray-900/40 p-2.5 space-y-2">
              {evidence!.snippet && (
                <blockquote className="italic text-gray-600 dark:text-gray-300 border-l-2 border-gray-300 dark:border-gray-600 pl-2">“{evidence!.snippet}”</blockquote>
              )}
              {evidence!.source && (
                <a href={evidence!.source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 underline break-all">
                  {evidence!.source}
                </a>
              )}
              {evidence!.prior !== undefined && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">{evidence!.priorLabel || 'Former value'}</div>
                  <div className="text-gray-500 dark:text-gray-400 line-through break-words">{evidence!.prior}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {actions && <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    </li>
  );
}

// The suggested fixes on a finding (current → suggested per field), without the actions.
function FixList({ fixes, caseData, onJump }: { fixes: Fix[]; caseData?: Record<string, any>; onJump?: (path: string) => void }) {
  return (
    <div className="space-y-3">
      {fixes.map((fix, i) => {
        const currentValue = caseData ? getValueByPath(caseData, fix.field) : undefined;
        const fixType = deriveFixType(fix.field, currentValue);
        return (
          <div key={i}>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
              <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${fixType.color}`}>{fixType.label}</span>
              <FieldLink path={fix.field} label={formatFieldPath(fix.field, caseData)} onJump={onJump} />
            </p>
            {fix.applied ? (
              <div>
                <RichValue value={fix.suggestedValue} />
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  <CheckCircleIcon className="h-3 w-3 inline mr-0.5" />
                  Applied{fix.appliedAt ? ` ${formatDistanceToNow(new Date(fix.appliedAt), { addSuffix: true })}` : ''}
                </p>
              </div>
            ) : isRecordArray(currentValue) || isRecordArray(fix.suggestedValue) ? (
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Current:</p>
                  <RichValue value={currentValue} />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Suggested:</p>
                  <RichValue value={fix.suggestedValue} />
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 text-xs">
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-500 dark:text-gray-400">Current:</span>
                  <span className={`ml-1 ${formatValue(currentValue) === 'none' ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                    {formatValue(currentValue)}
                  </span>
                </div>
                <span className="text-gray-400 dark:text-gray-500 shrink-0">&rarr;</span>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Suggested:</span>
                  <RichValue value={fix.suggestedValue} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// What the page-level header needs from the latest validation run, lifted through onSummary
// (mirrors DeduplicationPanel's onSummary) so CaseDetail can show it without its own query.
export interface ValidationSummary {
  decision: ValidationDecision | null;
  confidence: number | null;
  isValidating: boolean;
  blockers: number;
  anchorsPass: boolean | null;
  missingAnchors: string[];
  // Expected fields the last run found undocumented (validator path shapes). null = no run yet.
  missing: string[] | null;
  // The latest run's findings and web-search sources, for the page's evidence trace.
  findings: Finding[];
  aiSources: Array<{ url: string; type: string; title: string }>;
  // Every run for the case, newest first, for the Activity timeline.
  history: Array<{ id: string; decision: ValidationDecision; confidence: number; createdAt: string }>;
  // Informational AI notes the reviewer has not been shown as a blocker, conflict or applied value.
  notes: number;
}

interface AiValidationPanelProps {
  caseId: string;
  caseData?: Record<string, any>;
  onChange?: () => void;
  onSummary?: (summary: ValidationSummary) => void;
  // Scroll the page to the section showing a field path. When given, field labels in the
  // drawer become links that close the drawer and jump there.
  onJumpToField?: (path: string, origin?: JumpOrigin) => void;
  // Close the drawer and show the page's Evidence card (web sources live there now).
  onShowEvidence?: () => void;
  // Time of the last real content edit (from the audit log). Drives the stale-run notice.
  lastEditAt?: string | null;
  // When the case was approved or rejected, and by whom (from the audit log; null if unknown).
  decidedAt?: { at: string; by: string | null } | null;
  // Render inside one of the page's cards instead of as a sidebar card. Under review: a row with
  // `heading` at the left and the scores at the right (they open the details drawer); no shell, decision chip, task list or footer button — the page lists the open
  // items itself (through onSummary). Once the case is decided: `heading`, then the verification
  // as label/value rows in the Case Information style, with the record behind a link.
  embedded?: boolean;
  heading?: React.ReactNode;
}

const AiValidationPanel = forwardRef<AiValidationPanelHandle, AiValidationPanelProps>(function AiValidationPanel({ caseId, caseData, onChange, onSummary, onJumpToField, onShowEvidence, lastEditAt, decidedAt, embedded = false, heading }, ref) {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerShown, setDrawerShown] = useState(false); // drives the slide-in/out animation
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('all'); // internal nav tab inside the drawer
  const openDrawer = (tab: DrawerTab = 'all') => { setDrawerTab(tab); setModalOpen(true); requestAnimationFrame(() => setDrawerShown(true)); };
  const closeDrawer = () => { setDrawerShown(false); setTimeout(() => setModalOpen(false), 300); };
  // Close the drawer, then let the page scroll to the field's section.
  const jumpFromDrawer = onJumpToField ? (path: string) => { closeDrawer(); onJumpToField(path, { kind: 'ai', tab: activeDrawerTab }); } : undefined;
  // Coverage used to be its own slide-over; it is a drawer tab now, so nothing stacks over the drawer.
  const openCompleteness = () => openDrawer('coverage');
  useImperativeHandle(ref, () => ({ openDrawer: (tab?: DrawerTab) => openDrawer(tab ?? defaultTab()), openCompleteness }));
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [undoingField, setUndoingField] = useState<string | null>(null);
  // Inline override editor (Auto-Filled list): which field is being edited, its draft value, saving.
  const [editField, setEditField] = useState<string | null>(null);
  const [editVal, setEditVal] = useState<string>('');
  const [savingOverride, setSavingOverride] = useState(false);
  // A fill into an empty field has no meaningful "former value" — only enrichments/overwrites do.
  const hadPriorValue = (v: unknown): boolean => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return !['', 'not specified', 'unknown', 'n/a'].includes(v.trim().toLowerCase());
    if (Array.isArray(v)) return v.length > 0;
    return true;
  };
  // Escape closes the drawer.
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrawer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { user } = useAuth();

  // Validation runs async: the trigger returns 202 immediately while the worker processes in the
  // background. Track when we triggered so the button stays disabled (and we poll) until a fresh
  // result comes back — otherwise the button re-enables instantly and the user can double-fire it.
  const [triggeredAt, setTriggeredAt] = useState<number | null>(null);
  // True when a triggered run hit the polling cap without a fresh result — say so instead of
  // silently showing the previous run.
  const [timedOut, setTimedOut] = useState(false);

  const { data: validationDetail, isLoading } = useQuery<ValidationStatusResponse>({
    queryKey: ['validation-status', caseId],
    queryFn: () => validationApi.getStatus(caseId),
    refetchInterval: triggeredAt ? 5000 : false,
  });

  // Clear the in-flight flag once a newer result has landed, or after a safety cap (so a failed
  // run can't leave the button stuck).
  useEffect(() => {
    if (!triggeredAt) return;
    const latestTs = validationDetail?.latest?.createdAt ? new Date(validationDetail.latest.createdAt).getTime() : 0;
    if (latestTs > triggeredAt) { setTriggeredAt(null); setTimedOut(false); return; }
    const cap = setTimeout(() => { setTriggeredAt(null); setTimedOut(true); }, 120000);
    return () => clearTimeout(cap);
  }, [triggeredAt, validationDetail]);

  const validateMutation = useMutation({
    mutationFn: () => validationApi.validateCase(caseId, user?.id),
    onSuccess: (data) => {
      setTriggeredAt(Date.now());
      setTimedOut(false);
      if ('status' in data && data.status === 'processing') {
        showToast('success', 'Validation started — results will appear shortly');
      } else {
        showToast('success', 'Validation completed');
      }
      queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] });
    },
    onError: () => showToast('error', 'Validation failed'),
  });

  const applySuggestionsMutation = useMutation({
    mutationFn: (fields?: string[]) =>
      validationApi.applySuggestions(validationDetail!.latest!.id, fields),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] });
      const count = data.applied.length;
      showToast('success', `Applied ${count} suggestion${count !== 1 ? 's' : ''}`);
    },
    onError: () => showToast('error', 'Failed to apply suggestions'),
  });

  const isValidating = caseData?.validationStatus === 'VALIDATING' || validateMutation.isPending || triggeredAt != null;

  // Confirm before spending tokens; guard against double-fire while a run is in flight.
  const handleValidate = async () => {
    if (isValidating) return;
    const ok = await confirm({
      title: 'Re-run AI validation?',
      message: 'This runs an AI validation that uses tokens (billable) and can take up to a minute. Only run it when needed. Proceed?',
      confirmLabel: 'Proceed',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    validateMutation.mutate();
  };

  const latest = validationDetail?.latest;
  // Was the case's content edited after this run? Uses the audit log's "edit" events (field
  // changes only — not status, assignment or AI-review actions), so reviewer work inside this
  // panel never trips it. Decided cases don't show it; a stale run is no longer actionable there.
  const staleSince: string | null = (() => {
    if (!latest || !lastEditAt) return null;
    if (isEndState(caseData?.status)) return null;
    const runAt = new Date(latest.createdAt).getTime();
    const editAt = new Date(lastEditAt).getTime();
    return editAt > runAt + 60_000 ? lastEditAt : null;
  })();
  // Quiet footnote in the drawer only: the run predates later edits. No call to action —
  // re-running is deliberate and billable, not something every edit should prompt.
  const StaleNotice = () => staleSince ? (
    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5" role="note">
      <InformationCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>This run predates edits made to the case {formatDistanceToNow(new Date(staleSince), { addSuffix: true })}.</span>
    </p>
  ) : null;
  const historyCount = (validationDetail?.history?.length ?? 0) - 1;

  const findings = [...(latest?.aiResponse?.findings ?? [])].sort((a, b) => {
    const sevOrder = { blocker: 0, note: 1 };
    const sevA = sevOrder[a.severity] ?? 2;
    const sevB = sevOrder[b.severity] ?? 2;
    return sevA - sevB;
  });
  const pendingFixCount = findings.reduce((sum, f) => sum + (f.fixes ?? []).filter(fix => !fix.applied).length, 0);

  // Auto-applied fields (auto-apply feature): AI filled these into the record; shown here as the
  // run's ledger (also marked inline on the fields). Undo reverts to the prior value.
  const applied: any[] = ((caseData?.metadata?.ai?.autoApplied as any[]) ?? []).filter((a) => !a.undone && !a.superseded);
  const undoneEntries: any[] = ((caseData?.metadata?.ai?.autoApplied as any[]) ?? []).filter((a) => a.undone);
  // Map each field -> the finding's message + severity/category, from the stored result. This lets
  // the detail popup show severity/category for auto-fills and conflicts (whose stored metadata entry
  // may predate the backend that records them) by deriving from the finding that produced the fix.
  const messageByField: Record<string, string> = {};
  const metaByField: Record<string, { severity?: string; category?: string }> = {};
  for (const f of (latest?.aiResponse?.findings ?? [])) {
    const msg = (f as any).message;
    const meta = { severity: (f as any).severity, category: (f as any).category };
    const put = (fld?: string) => { if (fld) { if (msg) messageByField[fld] = msg; metaByField[fld] = meta; } };
    put((f as any).field);
    for (const fix of (f.fixes ?? [])) put(fix.field);
  }
  const undoApplied = async (field: string) => {
    setUndoingField(field);
    try {
      await validationApi.undoAutoApplied(caseId, field);
      showToast('success', 'Change undone');
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Undo failed');
    } finally {
      setUndoingField(null);
    }
  };
  const redoApplied = async (field: string) => {
    setUndoingField(field);
    try {
      await validationApi.redoAutoApplied(caseId, field);
      showToast('success', 'Change re-applied');
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Redo failed');
    } finally {
      setUndoingField(null);
    }
  };

  // Inline override of an auto-filled value. Scalar/boolean/array only — entity adds (new victim,
  // etc.) and nested objects are edited on the case form instead.
  const OVERRIDE_ADD_FIELDS = new Set(['victims', 'violationDetails', 'perpetrators', 'additionalSources']);
  // "Type" fields (violationTypes, otherViolationTypes, perpetratorTypes, …) are controlled enums —
  // they must be edited via the case form's dropdowns, not free-typed here.
  const isTypeField = (field: string) => /type/i.test((field.split('.').pop() || field).replace(/\[.*?\]/g, ''));
  const isOverridable = (a: any) => !OVERRIDE_ADD_FIELDS.has(a.field) && !isTypeField(a.field) && !(a.value && typeof a.value === 'object' && !Array.isArray(a.value));
  const startEdit = (a: any) => {
    setEditField(a.field);
    const v = a.value;
    setEditVal(Array.isArray(v) ? v.join(', ') : (v == null ? '' : String(v)));
  };
  const coerceOverride = (orig: any, s: string): any => {
    if (typeof orig === 'number') { const n = Number(s); return Number.isNaN(n) ? s : n; }
    if (typeof orig === 'boolean') return s === 'true';
    if (Array.isArray(orig)) return s.split(',').map((x) => x.trim()).filter(Boolean);
    return s;
  };
  const saveOverride = async (a: any) => {
    setSavingOverride(true);
    try {
      await validationApi.overrideAutoApplied(caseId, a.field, coerceOverride(a.value, editVal));
      showToast('success', 'Value updated');
      setEditField(null);
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Update failed');
    } finally {
      setSavingOverride(false);
    }
  };

  // Source conflicts (auto-apply feature): unresolved disagreements that block approval.
  const conflicts: any[] = ((caseData?.metadata?.ai?.conflicts as any[]) ?? []).filter((c) => !c.resolved);
  // On an approved or rejected case the panel is a record, not a worklist: no undo, edit,
  // confirm, resolve or re-run. The data stays readable for provenance.
  const decided = isEndState(caseData?.status);
  const canResolve = !decided && (user?.role === 'ADMIN' || user?.role === 'APPROVER');
  const canConfirm = !decided && (canResolve || user?.role === 'RESEARCHER');

  // Findings have no id, so a reviewer's decision about one is keyed by its content.
  const findingKey = (f: Finding) => `${f.severity}|${(f as any).category || ''}|${f.message}`;
  type FindingResolution = { key: string; resolution: 'addressed' | 'not_applicable'; note?: string; resolvedByName?: string; resolvedAt?: string };
  const findingResolutions: FindingResolution[] = ((caseData?.metadata?.ai?.findingResolutions as FindingResolution[]) ?? []);
  const resolutionFor = (f: Finding) => findingResolutions.find((r) => r.key === findingKey(f));
  const [resolvingKey, setResolvingKey] = useState<string | null>(null);         // entry with the note box open
  const [resolvingChoice, setResolvingChoice] = useState<'addressed' | 'not_applicable'>('addressed');
  const [resolvingNote, setResolvingNote] = useState('');
  const [savingResolution, setSavingResolution] = useState(false);
  const startResolve = (f: Finding, choice: 'addressed' | 'not_applicable') => { setResolvingKey(findingKey(f)); setResolvingChoice(choice); setResolvingNote(''); };
  const saveResolution = async (f: Finding) => {
    setSavingResolution(true);
    try {
      await validationApi.resolveFinding(caseId, findingKey(f), resolvingChoice, resolvingNote.trim() || undefined);
      showToast('success', resolvingChoice === 'addressed' ? 'Marked as addressed' : 'Marked as not applicable');
      setResolvingKey(null);
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Could not save the resolution');
    } finally {
      setSavingResolution(false);
    }
  };
  const reopenFinding = async (f: Finding) => {
    try {
      await validationApi.unresolveFinding(caseId, findingKey(f));
      showToast('success', 'Reopened');
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Could not reopen the finding');
    }
  };
  const [confirmingField, setConfirmingField] = useState<string | null>(null);
  const confirmApplied = async (field: string, confirmed: boolean) => {
    setConfirmingField(field);
    try {
      await validationApi.confirmAutoApplied(caseId, field, confirmed);
      showToast('success', confirmed ? 'Marked as checked against the source' : 'Confirmation withdrawn');
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Could not save confirmation');
    } finally {
      setConfirmingField(null);
    }
  };
  const resolveConflict = async (id: string, choice: 'current' | 'proposed' | 'custom', value?: unknown) => {
    setResolvingId(`${id}:${choice}`);
    try {
      await validationApi.resolveConflict(caseId, id, choice, value);
      showToast('success', 'Conflict resolved');
      setEditConflictId(null);
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Failed to resolve conflict');
    } finally {
      setResolvingId(null);
    }
  };
  // Inline "different value" editor on a conflict entry (one open at a time).
  const [editConflictId, setEditConflictId] = useState<string | null>(null);
  const [conflictVal, setConflictVal] = useState('');
  // A people count the source gives for the Indigenous group and non-Indigenous group(s) TOGETHER. The
  // proposed value is that total, so it is never offered as one group's count: the reviewer enters
  // each group's share, or leaves it empty. Mirrors UNSPLIT_COUNT in the backend's aiEnrichment.
  const isUnsplit = (c: any) => c.category === 'UNSPLIT_COUNT';
  const startConflictEdit = (c: any) => {
    setEditConflictId(c.id);
    const v = isUnsplit(c) ? '' : (c.proposedValue ?? c.currentValue);
    setConflictVal(Array.isArray(v) ? v.join(', ') : (v == null ? '' : String(v)));
  };
  // Accept every open proposal in one go — one confirmation, one refresh at the end.
  const resolveAllProposed = async () => {
    const open = conflicts.filter((c) => c.id && !isUnsplit(c));
    if (open.length === 0) return;
    const ok = await confirm({
      title: `Use all ${open.length} proposed values?`,
      message: 'Each open conflict will be resolved with the value the newer source proposed. You can still edit any field afterwards.',
      confirmLabel: 'Use all proposed',
    });
    if (!ok) return;
    setResolvingId('all');
    let done = 0;
    try {
      for (const c of open) {
        await validationApi.resolveConflict(caseId, c.id, 'proposed');
        done++;
      }
      showToast('success', `${done} conflict${done === 1 ? '' : 's'} resolved`);
    } catch (e: any) {
      showToast('error', `${done} of ${open.length} resolved, then: ${e.response?.data?.error || 'failed'}`);
    } finally {
      setResolvingId(null);
      onChange?.();
    }
  };
  const fmtVal = (v: unknown): React.ReactNode => {
    if (v === null || v === undefined || v === '') return <span className="italic text-gray-400 dark:text-gray-500">empty</span>;
    const asDate = formatIsoDate(v);
    if (asDate) return asDate;
    if (Array.isArray(v)) return v.map((x) => (x && typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  // Source URL as link text, protocol stripped and truncated to 50 chars.

  // A new victim/perpetrator/violation-detail record is added as an object — summarize it in
  // plain language instead of dumping JSON.
  const ADD_LABELS: Record<string, string> = { victims: 'victim', perpetrators: 'perpetrator', violationDetails: 'violation detail' };
  const entityName = (o: any): string => {
    if (!o || typeof o !== 'object') return String(o);
    const nm = [o.firstName, o.lastName].filter(Boolean).join(' ');
    if (nm) return nm;
    if (o.name) return String(o.name);
    if (o.groupName) return Array.isArray(o.groupName) ? o.groupName.join(', ') : String(o.groupName);
    if (o.violationType) return String(o.violationType);
    return 'record';
  };
  // Render an auto-applied value as the visual focal point of its row:
  //  - entity add  -> "New victim: <name>"
  //  - array/enum  -> chips, with elements NOT in the prior value highlighted (what changed)
  //  - scalar/text -> the value, prominent
  const renderAppliedValue = (a: any): React.ReactNode => {
    const { field, value, priorValue } = a;
    const addLabel = ADD_LABELS[field];
    if (addLabel) {
      const arr = Array.isArray(value) ? value : [value];
      return (
        <span className="inline-flex items-center gap-1 rounded bg-violet-50 dark:bg-violet-900/30 px-1.5 py-0.5 font-medium text-violet-800 dark:text-violet-300">
          + New {addLabel}: {arr.map(entityName).join('; ')}
        </span>
      );
    }
    if (Array.isArray(value)) {
      const prior = new Set((Array.isArray(priorValue) ? priorValue : []).map((x) => String(x)));
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => {
            const isNew = !prior.has(String(v));
            const text = isEnumValue(String(v)) ? formatEnumValue(String(v)) : String(v);
            return (
              <span key={i} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${isNew ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-300 ring-1 ring-violet-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                {isNew ? `+ ${text}` : text}
              </span>
            );
          })}
        </div>
      );
    }
    return <span className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">{fmtVal(value)}</span>;
  };

  // When auto-apply is active, applyable findings are already shown inline (auto-applied) or as
  // conflicts above — so the findings list collapses to just the informational items the AI could
  // not act on (no corroboration, "consider adding a victim", etc.).
  const allConflictFields = new Set(((caseData?.metadata?.ai?.conflicts as any[]) ?? []).map((c) => c.field).filter(Boolean));
  const autoApplyActive = ((caseData?.metadata?.ai?.autoApplied as any[])?.length ?? 0) > 0 || allConflictFields.size > 0;
  const visibleFindings = autoApplyActive
    ? findings.filter((f) => {
        const fixes = f.fixes ?? [];
        const ff = (f as any).field;
        const allApplied = fixes.length > 0 && fixes.every((x: any) => x.applied);
        const isConflict = (ff && allConflictFields.has(ff)) || fixes.some((x: any) => allConflictFields.has(x.field));
        return !allApplied && !isConflict;
      })
    : findings;

  // Blocker-severity findings without an auto-fix (e.g. a data conflict the AI couldn't
  // resolve) must stand out — surface them in their own section, not buried in "Other comments".
  const allVisibleBlockers = visibleFindings.filter((f) => f.severity === 'blocker');
  const resolvedBlockers = allVisibleBlockers.filter((f) => resolutionFor(f));
  const visibleBlockers = allVisibleBlockers.filter((f) => !resolutionFor(f)); // open ones drive counts and tabs
  const visibleNotes = visibleFindings.filter((f) => f.severity !== 'blocker');
  // In auto-apply mode blockers get their own section, so "Other comments" holds only notes.
  const otherCommentsList = autoApplyActive ? visibleNotes : visibleFindings;

  // Summary badge counts: when auto-apply is active, count only what still needs a human
  // (applied + conflicts are shown separately) — the remaining informational notes/blockers.
  const badgeSource = autoApplyActive ? visibleFindings : findings;
  const blockerCount = badgeSource.filter((f) => f.severity === 'blocker' && !resolutionFor(f)).length;

  // Report the header-level summary up whenever its inputs change.
  const summaryDecision = latest?.decision ?? null;
  const summaryConfidence = latest?.confidence ?? null;
  const summaryComp = latest?.aiResponse?.completeness;
  const summaryAnchorsPass = summaryComp?.anchorsPass ?? null;
  const summaryMissingKey = (summaryComp?.missingAnchors ?? []).join('|');
  const summaryGapsKey = summaryComp ? (summaryComp.missing ?? []).join('|') : null;
  const summaryNotes = otherCommentsList.length;
  useEffect(() => {
    onSummary?.({
      decision: summaryDecision,
      confidence: summaryConfidence,
      isValidating,
      blockers: blockerCount,
      anchorsPass: summaryAnchorsPass,
      missingAnchors: summaryMissingKey ? summaryMissingKey.split('|') : [],
      missing: summaryGapsKey === null ? null : (summaryGapsKey ? summaryGapsKey.split('|') : []),
      findings: latest?.aiResponse?.findings ?? [],
      aiSources: latest?.aiResponse?.sources ?? [],
      history: (validationDetail?.history ?? []).map((r) => ({ id: r.id, decision: r.decision, confidence: r.confidence, createdAt: r.createdAt })),
      notes: summaryNotes,
    });
  }, [onSummary, summaryDecision, summaryConfidence, isValidating, blockerCount, summaryAnchorsPass, summaryMissingKey, summaryGapsKey, summaryNotes, latest, validationDetail]);

  // Internal drawer tabs — turn the long scroll into a few focused views. Only tabs with content
  // appear (besides "All"); the active tab falls back to "All" if its content drops to zero.
  // A fixed tab set in a fixed order, so the drawer reads the same on every case; tabs with
  // nothing in them are shown disabled with 0 rather than removed.
  const NEUTRAL_PILL = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  const drawerTabDefs: { key: DrawerTab; label: string; count: number; pill: string }[] = latest ? [
    { key: 'all', label: 'All', count: (autoApplyActive ? visibleBlockers.length : 0) + conflicts.length + applied.length + otherCommentsList.length, pill: NEUTRAL_PILL },
    { key: 'blockers', label: 'Blockers', count: autoApplyActive ? visibleBlockers.length : 0, pill: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
    { key: 'resolution', label: 'For Resolution', count: conflicts.length, pill: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
    { key: 'autofilled', label: 'Auto-Filled', count: applied.length, pill: NEUTRAL_PILL },
    { key: 'notes', label: 'Notes', count: otherCommentsList.length, pill: NEUTRAL_PILL },
    { key: 'coverage', label: 'Coverage', count: latest.aiResponse?.completeness?.missing?.length ?? 0, pill: NEUTRAL_PILL },
    { key: 'history', label: 'History', count: Math.max(0, historyCount), pill: NEUTRAL_PILL },
  ] : [];
  const tabEnabled = (t: { key: DrawerTab; count: number }) => t.key === 'all' || (t.key === 'coverage' ? !!latest?.aiResponse?.completeness : t.count > 0);
  // What the reviewer still has to do, in priority order. Drives the card's task list and the
  // tab the drawer opens on by default.
  const unconfirmedApplied = applied.filter((a) => !a.confirmedAt).length;
  type Task = { key: DrawerTab; label: string; count: number; tone: 'danger' | 'warning' | 'neutral' };
  const taskDefs: Task[] = latest ? [
    { key: 'blockers', label: `blocker${(autoApplyActive ? visibleBlockers.length : 0) === 1 ? '' : 's'} to resolve`, count: autoApplyActive ? visibleBlockers.length : 0, tone: 'danger' },
    { key: 'resolution', label: `source conflict${conflicts.length === 1 ? '' : 's'} to resolve`, count: conflicts.length, tone: 'warning' },
    { key: 'autofilled', label: `AI-filled field${unconfirmedApplied === 1 ? '' : 's'} to check against the source`, count: unconfirmedApplied, tone: 'neutral' },
    { key: 'notes', label: `note${otherCommentsList.length === 1 ? '' : 's'} to read`, count: otherCommentsList.length, tone: 'neutral' },
  ] : [];
  const tasks = taskDefs.filter((t) => t.count > 0);
  const defaultTab = (): DrawerTab => tasks[0]?.key ?? 'all';
  // One-line "why" for the decision, from the deterministic deductions.
  const deductions = latest?.aiResponse?.confidenceBreakdown?.deductions ?? [];
  const whyLine = deductions.length
    ? `${deductions[0].message || deductions[0].category.replace(/_/g, ' ').toLowerCase().replace(/^./, (x) => x.toUpperCase())} (−${Math.round(deductions[0].weight * 100)}%)${deductions.length > 1 ? ` and ${deductions.length - 1} more` : ''}`
    : null;
  const activeDrawerTab: DrawerTab = drawerTabDefs.some((t) => t.key === drawerTab && tabEnabled(t)) ? drawerTab : 'all';
  const showTab = (t: DrawerTab) => activeDrawerTab === 'all' || activeDrawerTab === t;

  const timedOutNotice = timedOut && (
    <div className="mb-3 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 flex items-center gap-2" role="status">
      <span className="flex-1">A validation run started over two minutes ago and no result has arrived yet. It may still be running.</span>
      <button
        type="button"
        onClick={() => { setTimedOut(false); queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] }); }}
        className="shrink-0 font-medium underline hover:no-underline"
      >
        Check again
      </button>
    </div>
  );
  const runButton = (
    <button
      onClick={handleValidate}
      disabled={isValidating}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <ArrowPathIcon className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`} />
      {isValidating ? 'Validating...' : 'Run Validation'}
    </button>
  );
  // When the case was decided: the run and the decision, side by side.
  const decidedRecord = latest && (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
      <dt className="text-gray-500 dark:text-gray-400">AI check</dt>
      <dd className="text-gray-900 dark:text-gray-100">{formatIsoDate(latest.createdAt) || formatDistanceToNow(new Date(latest.createdAt), { addSuffix: true })}</dd>
      <dt className="text-gray-500 dark:text-gray-400">{caseData?.status === 'REJECTED' ? 'Rejected' : 'Approved'}</dt>
      <dd className="text-gray-900 dark:text-gray-100">
        {decidedAt ? `${formatIsoDate(decidedAt.at) || decidedAt.at}${decidedAt.by ? ` by ${decidedAt.by}` : ''}` : <span className="text-gray-400 dark:text-gray-500">Date not recorded</span>}
      </dd>
    </dl>
  );

  // Embedded in the page's AI card while the case is under review: the heading row with the scores
  // at its right, then the AI's reasoning for its decision. With no chip or links on the card, the
  // scores are the way into the details when nothing is open on the readiness side.
  const reviewView = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        {heading}
        {isValidating && !latest ? (
          <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <ArrowPathIcon className="h-4 w-4 text-blue-400 animate-spin" aria-hidden="true" /> Validation in progress...
          </p>
        ) : latest ? (
          <button
            type="button"
            onClick={() => openDrawer('all')}
            title="Open the AI verification details"
            className="flex flex-wrap items-start gap-x-6 gap-y-3 rounded-md -m-1.5 p-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            {labeledBar('Confidence', confidenceBar(latest.confidence, 'md'))}
            {labeledBar('Documentation', completenessBar(latest.aiResponse?.completeness, 'md'))}
          </button>
        ) : isLoading ? null : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">No validation results</p>
            {runButton}
          </div>
        )}
      </div>
      {timedOutNotice}
      {isLoading && !latest ? (
        <div className="animate-pulse space-y-2">
          <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
          <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded" />
        </div>
      ) : latest?.aiResponse?.reasoning ? (
        <p className="text-xs leading-5 text-gray-600 dark:text-gray-400">{latest.aiResponse.reasoning}</p>
      ) : null}
    </div>
  );

  // Embedded once the case is decided: the verification as a section of Case Information, in that
  // card's label/value style — the dates only; the scores and the rest are in the record.
  const dt = 'text-sm font-medium text-gray-500 dark:text-gray-400';
  const dd = 'text-sm text-gray-900 dark:text-gray-100 mt-1';
  const recordView = (
    <div className="space-y-3">
      {heading}
      {isLoading && !latest ? (
        <div className="animate-pulse space-y-2">
          <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
          <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded" />
        </div>
      ) : latest ? (
        <>
          <dl className="space-y-3">
            <div>
              <dt className={dt}>AI check</dt>
              <dd className={dd}>{formatIsoDate(latest.createdAt) || formatDistanceToNow(new Date(latest.createdAt), { addSuffix: true })}</dd>
            </div>
            <div>
              <dt className={dt}>{caseData?.status === 'REJECTED' ? 'Rejected' : caseData?.status === 'ARCHIVED' ? 'Archived' : 'Approved'}</dt>
              <dd className={dd}>
                {decidedAt ? `${formatIsoDate(decidedAt.at) || decidedAt.at}${decidedAt.by ? ` by ${decidedAt.by}` : ''}` : <span className="text-gray-400 dark:text-gray-500">Date not recorded</span>}
              </dd>
            </div>
          </dl>
          <button
            type="button"
            onClick={() => openDrawer('all')}
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 dark:text-blue-300 hover:underline"
          >
            View the record <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">No AI verification was run on this case before it was decided.</p>
      )}
    </div>
  );

  // The sidebar card: heading and decision chip, then the scores, the "why", the task list and a
  // footer button — or whichever state the panel is in.
  const sidebarCard = (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Assisted Verification</h3>
        {isValidating ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
            Validating
          </span>
        ) : latest ? (
          <div className="flex items-center gap-1.5">
            {decisionIcons[latest.decision]}
            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${decisionColors[latest.decision]}`}>
              {decisionLabel(latest.decision)}
            </span>
          </div>
        ) : null}
      </div>
      {timedOutNotice}

      {isValidating && !latest ? (
        <div className="text-center py-4">
          <ArrowPathIcon className="h-8 w-8 mx-auto text-blue-400 animate-spin mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Validation in progress...</p>
        </div>
      ) : isLoading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
          <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded" />
        </div>
      ) : latest && decided ? (
        <div className="space-y-3">
          {decidedRecord}
          <button
            type="button"
            onClick={() => openDrawer('all')}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-200 hover:underline"
          >
            View the record <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ) : latest ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {labeledBar('Confidence', confidenceBar(latest.confidence))}
            {labeledBar('Documentation', completenessBar(latest.aiResponse?.completeness))}
          </div>
          {whyLine && (
            <p className="text-xs text-gray-600 dark:text-gray-400"><span className="font-medium text-gray-700 dark:text-gray-300">Why: </span>{whyLine}</p>
          )}

          {/* Task list — each row opens the drawer on its tab */}
          {tasks.length > 0 ? (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700">
              {tasks.map((t) => (
                <li key={t.key}>
                  <button
                    type="button"
                    onClick={() => openDrawer(t.key)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <span className={t.tone === 'danger' ? 'text-red-700 dark:text-red-300 font-medium' : t.tone === 'warning' ? 'text-amber-800 dark:text-amber-300 font-medium' : 'text-gray-800 dark:text-gray-200'}>
                      <span className="tabular-nums">{t.count}</span> {t.label}
                    </span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <CheckCircleIcon className="h-4 w-4 text-gray-400" aria-hidden="true" /> Nothing left to act on from this run.
            </p>
          )}

          <button
            onClick={() => openDrawer('all')}
            className="w-full px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            View all details
          </button>
        </div>
      ) : decided ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No AI verification was run on this case before it was decided.</p>
      ) : (
        <div className="text-center py-2">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">No validation results</p>
          {runButton}
        </div>
      )}
    </div>
  );

  return (
    <>
      {embedded ? (decided ? recordView : reviewView) : sidebarCard}

      {/* Detail Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-gray-500/60 transition-opacity duration-300 ${drawerShown ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeDrawer}
          />
          {/* Right slide-over drawer */}
          <div
            className={`fixed inset-y-0 right-0 w-full max-w-4xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerShown ? 'translate-x-0' : 'translate-x-full'}`}
          >
            {/* Drawer Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Assisted Verification Details</h3>
              <button
                onClick={closeDrawer}
                aria-label="Close"
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                {latest ? (
                  <>
                    {!isValidating && <StaleNotice />}
                    {/* Decision, Confidence & Completeness */}
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg px-4 py-3 flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
                      <div className="flex items-center gap-2 pt-0.5">
                        {decisionIcons[latest.decision]}
                        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${decisionColors[latest.decision]}`}>
                          {decisionLabel(latest.decision)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{decided ? 'at the time of review, ' : ''}{formatDistanceToNow(new Date(latest.createdAt), { addSuffix: true })}</span>
                      </div>
                      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
                        <div>
                          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Confidence</div>
                          {confidenceBar(latest.confidence, 'lg')}
                          <div className="mt-1"><ScoreBreakdown breakdown={latest.aiResponse?.confidenceBreakdown} /></div>
                        </div>
                        {latest.aiResponse?.completeness && (
                          <div>
                            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                              Documentation
                              <button
                                type="button"
                                onClick={() => setDrawerTab('coverage')}
                                className="ml-1.5 font-medium text-gray-600 dark:text-gray-300 underline hover:text-gray-900 dark:hover:text-gray-100"
                              >
                                details
                              </button>
                            </div>
                            {completenessBar(latest.aiResponse.completeness, 'lg')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Internal navigation tabs — focus the view instead of one long scroll */}
                    {drawerTabDefs.length > 1 && (
                      <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav role="tablist" aria-label="Validation details" className="-mb-px flex gap-4 overflow-x-auto">
                          {drawerTabDefs.map((t) => (
                            <button
                              key={t.key}
                              role="tab"
                              aria-selected={activeDrawerTab === t.key}
                              aria-disabled={!tabEnabled(t)}
                              disabled={!tabEnabled(t)}
                              onClick={() => setDrawerTab(t.key)}
                              className={`whitespace-nowrap pb-2.5 pt-1 text-sm font-medium border-b-2 flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                activeDrawerTab === t.key
                                  ? 'border-gray-900 text-gray-900 dark:border-gray-100 dark:text-gray-100'
                                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                              }`}
                            >
                              {t.label}
                              <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs font-semibold ${t.pill}`}>{t.count}</span>
                            </button>
                          ))}
                        </nav>
                      </div>
                    )}

                    {/* Reasoning */}
                    {activeDrawerTab === 'all' && latest.aiResponse?.reasoning && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">AI Reasoning</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{latest.aiResponse.reasoning}</p>
                      </div>
                    )}

                    {/* Blockers without an auto-fix — first, always */}
                    {showTab('blockers') && autoApplyActive && (visibleBlockers.length > 0 || resolvedBlockers.length > 0) && (
                      <section>
                        {visibleBlockers.length > 0 && (
                          <>
                            <h4 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2 flex items-center gap-1.5">
                              <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                              {visibleBlockers.length} blocker{visibleBlockers.length !== 1 ? 's' : ''} to resolve before approving
                            </h4>
                            <ul className="space-y-2">
                              {visibleBlockers.map((finding, idx) => {
                                const k = findingKey(finding);
                                const editing = resolvingKey === k;
                                return (
                                  <ReviewEntry
                                    key={idx}
                                    tone="danger"
                                    pill="Blocker"
                                    category={(finding as any).category}
                                    caseData={caseData}
                                    onJump={jumpFromDrawer}
                                    message={finding.message}
                                    evidence={{ snippet: (finding as any).snippet, source: (finding as any).source }}
                                    value={editing ? (
                                      <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-1">
                                          {resolvingChoice === 'addressed' ? 'What was done about it' : 'Why it does not apply'} <span className="font-normal normal-case text-gray-400">(optional)</span>
                                        </div>
                                        <textarea value={resolvingNote} onChange={(e) => setResolvingNote(e.target.value)} rows={2} autoFocus className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100" />
                                      </div>
                                    ) : undefined}
                                    actions={canConfirm ? (editing ? (
                                      <>
                                        <button disabled={savingResolution} onClick={() => setResolvingKey(null)} className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50">Cancel</button>
                                        <button disabled={savingResolution} onClick={() => saveResolution(finding)} className="px-2.5 py-1 rounded-md bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">{savingResolution ? 'Saving…' : resolvingChoice === 'addressed' ? 'Mark addressed' : 'Mark not applicable'}</button>
                                      </>
                                    ) : (
                                      <>
                                        <button onClick={() => startResolve(finding, 'not_applicable')} title="The concern does not apply to this case" className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">Not applicable</button>
                                        <button onClick={() => startResolve(finding, 'addressed')} title="The concern has been dealt with" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 font-semibold text-white hover:bg-emerald-700"><CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" /> Addressed</button>
                                      </>
                                    )) : undefined}
                                  />
                                );
                              })}
                            </ul>
                          </>
                        )}
                        {resolvedBlockers.length > 0 && (
                          <details className={visibleBlockers.length > 0 ? 'mt-3' : ''}>
                            <summary className="cursor-pointer text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                              {resolvedBlockers.length} resolved blocker{resolvedBlockers.length === 1 ? '' : 's'}
                            </summary>
                            <ul className="mt-2 space-y-2">
                              {resolvedBlockers.map((finding, idx) => {
                                const r = resolutionFor(finding)!;
                                return (
                                  <ReviewEntry
                                    key={idx}
                                    tone="neutral"
                                    pill={r.resolution === 'addressed' ? 'Addressed' : 'Not applicable'}
                                    category={(finding as any).category}
                                    caseData={caseData}
                                    onJump={jumpFromDrawer}
                                    message={finding.message}
                                    meta={
                                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                        {r.note ? <span className="text-gray-700 dark:text-gray-300">{r.note} · </span> : null}
                                        {r.resolvedByName || 'A reviewer'}{r.resolvedAt && formatIsoDate(r.resolvedAt) ? `, ${formatIsoDate(r.resolvedAt)}` : ''}
                                      </span>
                                    }
                                    evidence={{ snippet: (finding as any).snippet, source: (finding as any).source }}
                                    actions={canConfirm ? (
                                      <button onClick={() => reopenFinding(finding)} className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">Reopen</button>
                                    ) : undefined}
                                  />
                                );
                              })}
                            </ul>
                          </details>
                        )}
                      </section>
                    )}

                    {/* Source conflicts — sources disagree on an existing value; blocks submission */}
                    {showTab('resolution') && conflicts.length > 0 && (
                      <section>
                        <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2 flex flex-wrap items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5">
                            <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                            {conflicts.length} source conflict{conflicts.length !== 1 ? 's' : ''} to resolve
                          </span>
                          {canResolve && conflicts.filter((c) => !isUnsplit(c)).length > 1 && (
                            <button
                              type="button"
                              disabled={!!resolvingId}
                              onClick={resolveAllProposed}
                              className="text-xs font-medium px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                              {resolvingId === 'all' ? 'Resolving…' : `Use all proposed (${conflicts.filter((c) => !isUnsplit(c)).length})`}
                            </button>
                          )}
                        </h4>
                        <ul className="space-y-2">
                          {conflicts.map((c, i) => (
                            <ReviewEntry
                              key={c.id || i}
                              tone="warning"
                              pill={isUnsplit(c) ? 'Combined count' : 'Conflict'}
                              category={c.category || metaByField[c.field]?.category}
                              field={c.field}
                              caseData={caseData}
                              onJump={jumpFromDrawer}
                              message={c.message || messageByField[c.field]}
                              value={editConflictId === c.id ? (
                                <div>
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-1">Your value</div>
                                  {isUnsplit(c) ? (
                                    <input
                                      type="number"
                                      min={0}
                                      max={Number(c.proposedValue)}
                                      value={conflictVal}
                                      onChange={(e) => setConflictVal(e.target.value)}
                                      autoFocus
                                      aria-label="Number of people in this group"
                                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                                    />
                                  ) : typeof (c.proposedValue ?? c.currentValue) === 'boolean' ? (
                                    <select value={conflictVal} onChange={(e) => setConflictVal(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100">
                                      <option value="true">Yes</option>
                                      <option value="false">No</option>
                                    </select>
                                  ) : (
                                    <textarea
                                      value={conflictVal}
                                      onChange={(e) => setConflictVal(e.target.value)}
                                      rows={Array.isArray(c.proposedValue) || String(c.proposedValue ?? '').length > 40 ? 3 : 1}
                                      autoFocus
                                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                                    />
                                  )}
                                  {Array.isArray(c.proposedValue ?? c.currentValue) && <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Separate multiple values with commas.</p>}
                                  {isUnsplit(c)
                                    ? <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">This group's share of the {fmtVal(c.proposedValue)} people the source reports for these groups together.</p>
                                    : <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Current: {fmtVal(c.currentValue)} · Proposed: {fmtVal(c.proposedValue)}</p>}
                                </div>
                              ) : isUnsplit(c) ? (
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-2">
                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">This group</div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400 break-words">{fmtVal(c.currentValue)}</div>
                                  </div>
                                  <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-2">
                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">All groups together</div>
                                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">{fmtVal(c.proposedValue)}</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-[1fr_auto_1.15fr] items-stretch gap-2">
                                  <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-2">
                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Current</div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400 line-through break-words">{fmtVal(c.currentValue)}</div>
                                  </div>
                                  <div className="flex items-center text-gray-400 dark:text-gray-500"><ArrowRightIcon className="h-4 w-4" aria-hidden="true" /></div>
                                  <div className="rounded-md border-2 border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800 p-2">
                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-0.5">Proposed</div>
                                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">{fmtVal(c.proposedValue)}</div>
                                  </div>
                                </div>
                              )}
                              evidence={{ snippet: c.snippet, source: c.source }}
                              actions={canResolve && c.id ? (
                                editConflictId === c.id ? (
                                  <>
                                    <button disabled={!!resolvingId} onClick={() => setEditConflictId(null)} className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50">Cancel</button>
                                    <button disabled={!!resolvingId || !conflictVal.trim()} onClick={() => resolveConflict(c.id, 'custom', coerceOverride(c.proposedValue ?? c.currentValue, conflictVal))} className="px-2.5 py-1 rounded-md bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">{resolvingId === `${c.id}:custom` ? 'Saving…' : 'Save this value'}</button>
                                  </>
                                ) : isUnsplit(c) ? (
                                  <>
                                    <button disabled={!!resolvingId} onClick={() => resolveConflict(c.id, 'current')} className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50">Not known — leave empty</button>
                                    <button disabled={!!resolvingId} onClick={() => startConflictEdit(c)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" /> Enter this group's number</button>
                                  </>
                                ) : (
                                  <>
                                    <button disabled={!!resolvingId} onClick={() => resolveConflict(c.id, 'current')} className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50">Keep current</button>
                                    <button disabled={!!resolvingId} onClick={() => startConflictEdit(c)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 disabled:opacity-50"><PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" /> Different value</button>
                                    <button disabled={!!resolvingId} onClick={() => resolveConflict(c.id, 'proposed')} className="px-2.5 py-1 rounded-md bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Use proposed</button>
                                  </>
                                )
                              ) : undefined}
                            />
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* Auto-applied fields — AI filled these; also marked inline on the fields */}
                    {showTab('autofilled') && applied.length > 0 && (
                      <section>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-1.5">
                          <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                          {applied.length} field{applied.length !== 1 ? 's' : ''} auto-filled by AI from related sources
                        </h4>
                        <ul className="space-y-2">
                          {applied.map((a, i) => (
                            <ReviewEntry
                              key={i}
                              tone="neutral"
                              pill={a.confirmedAt ? 'AI · checked' : 'AI-filled'}
                              category={metaByField[a.field]?.category}
                              field={a.field}
                              caseData={caseData}
                              onJump={jumpFromDrawer}
                              message={a.message || messageByField[a.field]}
                              messageAsNote
                              value={editField === a.field ? (
                                <div className="rounded-md border-2 border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800 p-2">
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-1">Your value</div>
                                  {typeof a.value === 'boolean' ? (
                                    <select value={editVal} onChange={(e) => setEditVal(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100">
                                      <option value="true">Yes</option>
                                      <option value="false">No</option>
                                    </select>
                                  ) : (
                                    <textarea
                                      value={editVal}
                                      onChange={(e) => setEditVal(e.target.value)}
                                      rows={Array.isArray(a.value) || String(a.value ?? '').length > 40 ? 3 : 1}
                                      autoFocus
                                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                                    />
                                  )}
                                  {Array.isArray(a.value) && <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Separate multiple values with commas.</p>}
                                </div>
                              ) : (
                                <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-2">
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">Value now on the case</div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{renderAppliedValue(a)}</div>
                                </div>
                              )}
                              meta={a.confirmedAt ? (
                                <span className="inline-flex items-center gap-1 text-[11px] text-green-700 dark:text-green-300">
                                  <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                  Checked{a.confirmedByName ? ` by ${a.confirmedByName}` : ''}{formatIsoDate(a.confirmedAt) ? `, ${formatIsoDate(a.confirmedAt)}` : ''}
                                </span>
                              ) : undefined}
                              evidence={{ snippet: a.snippet, source: a.source, prior: hadPriorValue(a.priorValue) ? fmtVal(a.priorValue) : undefined }}
                              actions={editField === a.field ? (
                                <>
                                  <button onClick={() => setEditField(null)} disabled={savingOverride} className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50">Cancel</button>
                                  <button onClick={() => saveOverride(a)} disabled={savingOverride} className="px-2.5 py-1 rounded-md bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">{savingOverride ? 'Saving…' : 'Save'}</button>
                                </>
                              ) : (canConfirm || canResolve) ? (
                                <>
                                  {canResolve && (
                                    <button disabled={undoingField === a.field} onClick={() => undoApplied(a.field)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                                      <ArrowPathIcon className="h-4 w-4" aria-hidden="true" /> Undo
                                    </button>
                                  )}
                                  {canResolve && isOverridable(a) && (
                                    <button onClick={() => startEdit(a)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                                      <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" /> Edit
                                    </button>
                                  )}
                                  {canConfirm && (
                                    a.confirmedAt ? (
                                      <button disabled={confirmingField === a.field} onClick={() => confirmApplied(a.field, false)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                                        Withdraw check
                                      </button>
                                    ) : (
                                      <button disabled={confirmingField === a.field} onClick={() => confirmApplied(a.field, true)} title="I checked this value against the source and it is correct" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                                        <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" /> Confirm
                                      </button>
                                    )
                                  )}
                                </>
                              ) : undefined}
                            />
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* Undone auto-applies — re-appliable */}
                    {showTab('autofilled') && undoneEntries.length > 0 && (
                      <section>
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{undoneEntries.length} undone</h4>
                        <ul className="space-y-2">
                          {undoneEntries.map((a, i) => (
                            <ReviewEntry
                              key={i}
                              tone="neutral"
                              pill="Undone"
                              field={a.field}
                              caseData={caseData}
                              onJump={jumpFromDrawer}
                              message="The previous value was restored."
                              evidence={{ prior: fmtVal(a.value), priorLabel: 'AI value that was undone', source: a.source, snippet: a.snippet }}
                              actions={canResolve ? (
                                <button disabled={undoingField === a.field} onClick={() => redoApplied(a.field)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                                  <ArrowPathIcon className="h-4 w-4" aria-hidden="true" /> Redo
                                </button>
                              ) : undefined}
                            />
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* Notes and findings */}
                    {showTab('notes') && otherCommentsList.length > 0 && (
                      <section>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center justify-between">
                          <span>Notes ({otherCommentsList.length})</span>
                          {!autoApplyActive && pendingFixCount > 0 && (
                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">{pendingFixCount} suggestion{pendingFixCount === 1 ? '' : 's'} pending</span>
                          )}
                        </h4>
                        <ul className="space-y-2">
                          {otherCommentsList.map((finding, idx) => {
                            const isBlocker = finding.severity === 'blocker';
                            const fixes = finding.fixes ?? [];
                            const pending = fixes.filter((f) => !f.applied);
                            const allApplied = fixes.length > 0 && pending.length === 0;
                            return (
                              <ReviewEntry
                                key={idx}
                                tone={isBlocker && !allApplied ? 'danger' : 'neutral'}
                                pill={isBlocker ? 'Blocker' : allApplied ? 'Applied' : 'Note'}
                                category={(finding as any).category}
                                caseData={caseData}
                                onJump={jumpFromDrawer}
                                message={finding.message}
                                messageHref={autoApplyActive && fixes.length === 0 ? (finding as any).source : undefined}
                                value={!autoApplyActive && fixes.length > 0 ? <FixList fixes={fixes} caseData={caseData} onJump={jumpFromDrawer} /> : undefined}
                                evidence={{ snippet: (finding as any).snippet, source: (finding as any).source }}
                                actions={!autoApplyActive && canResolve && pending.length > 0 ? (
                                  <button
                                    onClick={() => applySuggestionsMutation.mutate(pending.map((f) => f.field))}
                                    disabled={applySuggestionsMutation.isPending}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                                  >
                                    {pending.length === 1 ? 'Apply fix' : `Apply ${pending.length} fixes`}
                                  </button>
                                ) : undefined}
                              />
                            );
                          })}
                        </ul>
                      </section>
                    )}

                    {/* Coverage — how fully the case is documented (its own tab; not part of All) */}
                    {activeDrawerTab === 'coverage' && latest.aiResponse?.completeness && (
                      <section className="space-y-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">How fully the case is documented against IPRI's Case Baseline / Data Dictionary. This is informational: a low figure is not a to-do list.</p>
                        <div>
                          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">{latest.aiResponse.completeness.filled} of {latest.aiResponse.completeness.total} expected fields present</div>
                          {completenessBar(latest.aiResponse.completeness, 'lg')}
                        </div>
                        {latest.aiResponse.completeness.anchorsPass !== undefined && (
                          <div>
                            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Required anchors</div>
                            <AnchorsBadge comp={latest.aiResponse.completeness} />
                          </div>
                        )}
                        {latest.aiResponse.completeness.missing.length > 0 && (
                          <div>
                            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Not documented ({latest.aiResponse.completeness.missing.length})</div>
                            <div className="space-y-2">
                              {groupMissing(latest.aiResponse.completeness.missing).map((g) => (
                                <div key={g.section}>
                                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">{g.section}</div>
                                  <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5 pl-1">
                                    {g.items.map((it) => (
                                      <li key={it.label} className="flex items-center gap-1.5">
                                        <span className="text-gray-300 dark:text-gray-600">•</span>
                                        {it.label}
                                        {it.count > 1 && <span className="text-gray-400 dark:text-gray-500">×{it.count}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </section>
                    )}

                    {/* Web sources live in the page's Evidence card */}
                    {activeDrawerTab === 'all' && (latest.aiResponse?.sources?.length ?? 0) > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-50 dark:bg-gray-900/40 px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                        <span>The {latest.aiResponse!.sources!.length} web source{latest.aiResponse!.sources!.length === 1 ? '' : 's'} the AI consulted are listed in the Evidence card on the page.</span>
                        {onShowEvidence && (
                          <button type="button" onClick={() => { closeDrawer(); onShowEvidence(); }} className="font-medium underline hover:no-underline">Show Evidence card</button>
                        )}
                      </div>
                    )}

                    {/* History */}
                    {showTab('history') && historyCount > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Previous Results ({historyCount})
                        </span>
                        <div className="mt-3 space-y-2">
                            {validationDetail!.history.slice(1).map((result) => (
                              <div
                                key={result.id}
                                className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-lg px-4 py-3"
                              >
                                <div className="flex items-center gap-2">
                                  {decisionIcons[result.decision]}
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{decisionLabel(result.decision)}</span>
                                </div>
                                <div className="flex items-center gap-4">
                                  {confidenceBar(result.confidence)}
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDistanceToNow(new Date(result.createdAt), { addSuffix: true })}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                      </div>
                    )}

                    {/* Re-validate — AI action, Approver/Admin only */}
                    {canResolve && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <button
                          onClick={handleValidate}
                          disabled={isValidating}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowPathIcon className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`} />
                          {isValidating ? 'Validating...' : 'Re-validate'}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    {isValidating ? (
                      <>
                        <ArrowPathIcon className="h-12 w-12 mx-auto text-blue-400 animate-spin mb-3" />
                        <p className="text-gray-500 dark:text-gray-400">Validation in progress...</p>
                      </>
                    ) : (
                      <>
                        <ShieldCheckIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500 dark:text-gray-400">No validation results for this case</p>
                        {canResolve && (
                          <button
                            onClick={handleValidate}
                            disabled={isValidating}
                            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                            Run Validation
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
      )}

    </>
  );
});

export default AiValidationPanel;
